import Component from "@glimmer/component";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { pageTitle } from "ember-page-title";
import type Owner from "@ember/owner";
import type SequencerService from "#services/sequencer.ts";
import Player from "#components/player.gts";
import EventLog, { EventLogger } from "#components/sandbox/event-log.gts";
import { Song } from "#models/song.ts";
import { Channel } from "#models/channel.ts";
import { Step } from "#models/step.ts";
import { Sound } from "#models/sound.ts";
import { PRESETS } from "#models/data/presets.ts";
import { deserializeSong, serializeSong } from "#utils/serialization.ts";

const eq = <T,>(a: T, b: T): boolean => a === b;

interface DemoSong {
  id: string;
  name: string;
  createSong: () => Song;
}

const DEMO_SONGS: readonly DemoSong[] = [
  {
    id: "basic",
    name: "Basic 4-Track",
    createSong: () =>
      new Song({
        name: "Basic 4-Track Demo",
        tempo: 120,
        channels: [
          new Channel({
            sound: Sound.findById("kick")!,
            volume: 1.0,
            steps: Array.from(
              { length: 16 },
              (_, i) => new Step({ velocity: i % 4 === 0 ? 1.0 : 0 }),
            ),
          }),
          new Channel({
            sound: Sound.findById("snare")!,
            volume: 0.9,
            steps: Array.from(
              { length: 16 },
              (_, i) => new Step({ velocity: i === 4 || i === 12 ? 1.0 : 0 }),
            ),
          }),
          new Channel({
            sound: Sound.findById("hihat_closed")!,
            volume: 0.7,
            steps: Array.from(
              { length: 16 },
              (_, i) => new Step({ velocity: i % 2 === 0 ? 0.5 : 0 }),
            ),
          }),
          new Channel({
            sound: Sound.findById("cowbell")!,
            volume: 0.8,
            steps: [
              new Step({ velocity: 1.0 }),
              new Step({ velocity: 0 }),
              new Step({ velocity: 0 }),
              new Step({ velocity: 0.5 }),
            ],
          }),
        ],
      }),
  },
  {
    id: "polyrhythm",
    name: "Polyrhythm / Odd Meter",
    createSong: () =>
      new Song({
        name: "Polyrhythm Demo",
        tempo: 128,
        channels: [
          new Channel({
            sound: Sound.findById("kick")!,
            steps: Array.from({ length: 4 }, (_, i) => new Step({ velocity: i === 0 ? 1.0 : 0 })),
          }),
          new Channel({
            sound: Sound.findById("snare")!,
            steps: Array.from({ length: 5 }, (_, i) => new Step({ velocity: i === 2 ? 1.0 : 0 })),
          }),
          new Channel({
            sound: Sound.findById("hihat_closed")!,
            steps: Array.from(
              { length: 7 },
              (_, i) => new Step({ velocity: i % 2 === 0 ? 0.5 : 0 }),
            ),
          }),
          new Channel({
            sound: Sound.findById("cowbell")!,
            steps: Array.from({ length: 3 }, (_, i) => new Step({ velocity: i === 0 ? 1.0 : 0 })),
          }),
        ],
      }),
  },
  {
    id: "minimal",
    name: "1-Channel Minimal",
    createSong: () =>
      new Song({
        name: "Minimal Kick",
        tempo: 110,
        channels: [
          new Channel({
            sound: Sound.findById("kick")!,
            steps: Array.from(
              { length: 16 },
              (_, i) => new Step({ velocity: i % 4 === 0 ? 1.0 : 0 }),
            ),
          }),
        ],
      }),
  },
  {
    id: "empty",
    name: "Empty Song",
    createSong: () =>
      new Song({
        name: "Empty Song",
        tempo: 120,
        channels: [],
      }),
  },
];

export default class SandboxPlayerTemplateComponent extends Component {
  @service declare sequencer: SequencerService;

  @tracked activePresetName = "NONE";
  logger = new EventLogger();

  constructor(owner: Owner, args: object) {
    super(owner, args);
    this.loadDemo(DEMO_SONGS[0]!);
  }

  get song(): Song | null {
    return this.sequencer.song;
  }

  get serializedSong() {
    return serializeSong(this.song!);
  }

  loadDemo = (demo: DemoSong) => {
    this.activePresetName = demo.name;
    const song = demo.createSong();
    this.sequencer.loadSong(song);
    this.logger.log(`Loaded preset: ${demo.name}`);
  };

  loadBuiltinPreset = (presetId: string, name: string) => {
    const presetDef = PRESETS.find((p) => p.id === presetId);
    if (!presetDef) return;
    try {
      const song = deserializeSong(presetDef.data);
      this.activePresetName = `Built-in: ${name}`;
      this.sequencer.loadSong(song);
      this.logger.log(`Loaded built-in preset: ${name}`);
    } catch (err) {
      this.logger.log(`Error loading preset ${name}: ${err}`);
    }
  };

  handleManualTick = () => {
    this.sequencer.tick();
    this.logger.log(`Manual single step tick -> tick ${this.sequencer.currentTick}`);
  };

  handleResetTick = () => {
    this.sequencer.reset();
    this.logger.log("Reset sequencer tick to 0");
  };

  handleStop = () => {
    this.sequencer.stop();
    this.logger.log("Sequencer stopped");
  };

  handleSongUpdated = () => {
    this.logger.log("onSongUpdated callback fired");
  };

  willDestroy(): void {
    super.willDestroy();
    this.sequencer.stop();
  }

  <template>
    {{pageTitle "Player Sandbox"}}

    <div class="space-y-6" data-test-player-sandbox>
      {{! Preset Switcher Bar }}
      <section class="space-y-2 border-b border-white pb-4">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Song Presets
          </span>
          <span class="text-xs text-amber-400 font-mono">
            Active:
            {{this.activePresetName}}
          </span>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          {{#each DEMO_SONGS as |demo|}}
            <button
              type="button"
              class="px-2.5 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-white cursor-pointer transition-colors"
              data-test-sandbox-load-demo={{demo.id}}
              {{on "click" (fn this.loadDemo demo)}}
            >
              {{demo.name}}
            </button>
          {{/each}}

          {{#if PRESETS.length}}
            <span class="text-slate-600 text-xs px-1">|</span>
            {{#each PRESETS as |preset|}}
              <button
                type="button"
                class="px-2.5 py-1 text-xs bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white cursor-pointer transition-colors"
                {{on "click" (fn this.loadBuiltinPreset preset.id preset.name)}}
              >
                {{preset.name}}
              </button>
            {{/each}}
          {{/if}}
        </div>
      </section>

      <section class="min-h-16">
        <div class="font-mono text-xs wrap-break-word">{{this.serializedSong}}</div>
      </section>

      {{! Rendered Player Component }}
      <section class="border border-white overflow-hidden">
        <Player @onSongUpdated={{this.handleSongUpdated}} />
      </section>

      {{! Diagnostics & State Strip }}
      <section class="border-t border-white pt-4 space-y-4 text-xs text-slate-400">
        <div class="flex items-center justify-between">
          <span class="font-semibold uppercase tracking-wider text-slate-500">Sequencer service</span>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-6 font-mono">
            <div>
              <span>isPlaying:</span>
              <span
                class="font-bold {{if this.sequencer.isPlaying 'text-green-400' 'text-slate-300'}}"
              >
                {{if this.sequencer.isPlaying "true" "false"}}
              </span>
            </div>
            <div>
              <span>currentTick:</span>
              <span class="font-bold text-amber-400">
                {{if (eq this.sequencer.currentTick null) "null" this.sequencer.currentTick}}
              </span>
            </div>
            <div>
              <span>tempo:</span>
              <span class="font-bold text-white">{{this.song.tempo}} bpm</span>
            </div>
            <div>
              <span>intervalMs:</span>
              <span class="font-bold text-white">{{this.sequencer.intervalMs}} ms</span>
            </div>
            <div>
              <span>channels:</span>
              <span class="font-bold text-white">{{this.song.channels.length}}</span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 cursor-pointer"
              {{on "click" this.handleManualTick}}
            >
              Single Tick
            </button>
            <button
              type="button"
              class="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 cursor-pointer"
              {{on "click" this.handleResetTick}}
            >
              Reset Tick
            </button>
            <button
              type="button"
              class="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 cursor-pointer"
              {{on "click" this.handleStop}}
            >
              Stop
            </button>
          </div>
        </div>

        {{! Event Log }}
        <EventLog @logger={{this.logger}} class="h-32" />
      </section>
    </div>
  </template>
}
